import { useEffect } from "react";
import { listPermissionsCatalog } from "@/api/projects";
import { PERMISSION } from "@/lib/permissions";

/**
 * Dev-only guard against permission drift between this app and the
 * backend catalog.
 *
 * Every gate in `usePermissions` is a string compared against the keys
 * the user's role carries. When the backend renames or drops a key and
 * this side keeps the old spelling, `has()` simply returns false: no
 * error, no 4xx, no console noise — the feature behind the gate just
 * stops being reachable for every role, including Admin. That is how
 * `views.create` stayed dead in production long enough to be noticed
 * by an audit rather than by the app.
 *
 * Two sibling keys make the case for checking this mechanically:
 * `activity.log_call` was dropped server-side in favour of `calls.log`,
 * while its twin `activity.log_note` was kept alongside `notes.edit`.
 * Identical-looking keys, opposite fates, nothing anywhere announcing
 * the difference.
 *
 * This is the cheap half of the fix and it only reports. The real gate
 * is a build-time check against a versioned catalog artifact the
 * backend commits — see `docs/permissions-contract.md`.
 *
 * Compiled out of production builds: Vite replaces `import.meta.env.DEV`
 * with a literal `false`, so the body is dead code in a prod bundle.
 */

/** Slugs already checked in this page session. Set before the request
 *  resolves, so a StrictMode double-mount (and a failed request) costs
 *  one round-trip rather than a retry loop. */
const checkedSlugs = new Set<string>();

export function usePermissionDrift(slug: string | undefined): void {
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!slug || checkedSlugs.has(slug)) return;
    checkedSlugs.add(slug);

    let cancelled = false;
    listPermissionsCatalog(slug)
      .then((categories) => {
        if (cancelled) return;
        const served = new Set<string>();
        for (const category of categories) {
          for (const permission of category.permissions) {
            served.add(permission.key);
          }
        }
        // An empty catalog means the endpoint answered but has nothing
        // to say (older backend, misconfigured project). Reporting every
        // key as an orphan there would be pure noise.
        if (served.size === 0) return;

        const orphans = Object.values(PERMISSION)
          .filter((key) => !served.has(key))
          .sort();
        if (orphans.length === 0) return;

        console.warn(
          `[permissions] ${orphans.length} key(s) this app gates on are absent ` +
            `from the backend catalog for "${slug}":\n` +
            orphans.map((key) => `  · ${key}`).join("\n") +
            `\nA gate on a key the catalog does not serve can never be ` +
            `granted, so the feature behind it is unreachable for every ` +
            `role. Either it was renamed server-side and this side kept ` +
            `the old spelling, or it has not shipped yet — in which case ` +
            `the gate needs a documented fallback, as canExportContacts has.`,
        );
      })
      .catch(() => {
        // Purely a development aid: a failed catalog fetch must never
        // surface as an error, and the caller has nothing to retry.
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);
}
